import React from 'react';
import { useTranslator } from 'tradera-lang/translate';
import GtmService from 'static/script/app/ui/google-tagmanager-service';
import InfoIcon from '@tradera/blueprint/theme/icons2/info.svg?jsx';
import PenIcon from '@tradera/blueprint/theme/icons2/pen.svg?jsx';
import SwishIcon from '@tradera/blueprint/theme/icons/swish.svg?jsx';
import WarningIcon from '@tradera/blueprint/theme/icons/warning.svg?jsx';

const trackPaymentOnboarding = (action, label) => {
    GtmService.trackAction('Tradera Payment Onboarding', action, label);
};

const InvoiceUnpaidAlert = () => {
    const { t } = useTranslator();
    return (
        <div className="alert d-flex mx-2 text-hyphenate">
            {t('siteheader_invoiceUnpaidDescription')}
        </div>
    );
};

const InvoiceOverdueAlert = () => {
    const { t } = useTranslator();
    return (
        <div className="alert alert-danger d-flex mx-2 my-1 text-hyphenate">
            <WarningIcon className="icon mr-2 flex-shrink-0" />
            <span>
                <strong>{t('siteheader_invoiceOverdueTitle')}</strong>
                <br />
                {t('siteheader_invoiceOverdueDescription')}
            </span>
        </div>
    );
};

const BankAccountMissingAlert = () => {
    const { t } = useTranslator();
    trackPaymentOnboarding(
        'Bank account Onboarding Alert',
        'Will add from - Account menu alert'
    );
    return (
        <div className="alert alert-info d-flex mx-2 my-1 text-hyphenate">
            <InfoIcon className="icon mt-1 mr-2 flex-shrink-0" />
            <span>
                <strong>{t('siteheader_bankAccountMissingTitle')}</strong>
                <br />
                {t('siteheader_bankAccountMissingDescription')}
            </span>
        </div>
    );
};

const PayoutFailedAlert = () => {
    const { t } = useTranslator();
    return (
        <div className="alert alert-danger d-flex mx-2 my-1 text-hyphenate">
            <WarningIcon className="icon mt-1 mr-2 flex-shrink-0" />
            <span>
                <strong>{t('siteheader_payoutFailedTitle')}</strong>
                <br />
                {t('siteheader_payoutFailedDescription')}
            </span>
        </div>
    );
};

const PayoutReadyBankAccountMissingAlert = () => {
    const { t } = useTranslator();
    trackPaymentOnboarding(
        'Bank account Onboarding Alert',
        'Will add from - Account menu alert - Failed payment'
    );
    return (
        <div className="alert alert-danger d-flex flex-column mx-2 my-1 text-hyphenate">
            <div className="d-flex">
                <WarningIcon className="icon mt-1 mr-2 flex-shrink-0" />
                <span>
                    <strong>
                        {t('siteheader_paymentReadBankAccountMissingTitle')}
                    </strong>
                    <br />
                    {t('siteheader_paymentReadBankAccountMissingDescription')}
                </span>
            </div>
        </div>
    );
};

const SwishMissingAlert = ({ dismissSwish, enableSwishUrl }) => {
    const { t } = useTranslator();
    trackPaymentOnboarding(
        'Swish Onboarding Alert',
        'Show on - Account menu alert'
    );
    return (
        <div className="alert alert-danger d-flex flex-column mx-2 my-1 text-hyphenate">
            <div className="d-flex mb-1">
                <SwishIcon className="icon mt-1 mr-2 flex-shrink-0" />
                <span>
                    <strong>{t('siteheader_swishMissingTitle')}</strong>
                    <br />
                    {t('siteheader_swishMissingDescription')}
                </span>
            </div>
            <button
                className="btn btn-outline-light border-white btn--icon-fixed btn-fluid mb-1"
                onClick={e => {
                    e.preventDefault();
                    window.location.href = enableSwishUrl;
                    trackPaymentOnboarding(
                        'Swish Onboarding Alert',
                        'Will add from - Account menu alert'
                    );
                }}>
                <PenIcon className="icon icon-before" />
                {t('siteheader_addSwish')}
            </button>
            <div
                className="text-center text-white text-wrap cursor-pointer"
                onClick={e => {
                    e.preventDefault();
                    dismissSwish();
                    trackPaymentOnboarding(
                        'Swish Onboarding Alert',
                        'Dismissed from - Account menu alert'
                    );
                }}>
                <u>{t('siteheader_swishNoThanks')}</u>
            </div>
        </div>
    );
};

const HyperwalletPayeeVerificationAlert = () => {
    const { t } = useTranslator();
    return (
        <div className="alert alert-danger d-flex flex-column mx-2 my-1 text-hyphenate">
            <div className="d-flex mb-1">
                <WarningIcon className="icon mt-1 mr-2 flex-shrink-0" />
                <span>
                    <strong>
                        {t('siteheader_hyperwalletPayeeVerificationTitle')}
                    </strong>
                    <br />
                    {t('siteheader_hyperwalletPayeeVerificationDescription')}
                </span>
            </div>
            <button
                className="btn btn-outline-light btn--icon-fixed btn-fluid mb-1"
                onClick={e => {
                    e.preventDefault();
                    window.location.href = '/my/PayeeVerification';
                }}>
                {t('siteheader_hyperwalletPayeeVerificationMoreInfo')}
            </button>
        </div>
    );
};

const HyperwalletPayeeVerificationReviewAlert = () => {
    const { t } = useTranslator();
    return (
        <div
            className="alert alert-info d-flex mx-2 my-1 text-hyphenate"
            onClick={e => {
                e.preventDefault();
                window.location.href = '/my/PayeeVerificationCompleted';
            }}>
            <WarningIcon className="icon mt-1 mr-2 flex-shrink-0" />
            <span>
                <strong>
                    {t('siteheader_hyperwalletPayeeVerificationReviewTitle')}
                </strong>
                <br />
                {t('siteheader_hyperwalletPayeeVerificationReviewDescription')}
            </span>
        </div>
    );
};

const FeeNettingInfoAlert = () => {
    const { t } = useTranslator();
    return (
        <div className="alert alert-info d-flex mx-2 my-1 text-hyphenate">
            <InfoIcon className="icon mt-1 mr-2 flex-shrink-0" />
            <span>
                <strong className="d-block mb-1">
                    {t('siteheader_feeNettingInfoTitle')}
                </strong>
                <span
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                        __html: t('siteheader_feeNettingInfoDescription')
                    }}
                />
            </span>
        </div>
    );
};

const RemoveSwishNumberAlert = ({ removeSwishNumberUrl }) => {
    const { t } = useTranslator();
    return (
        <div
            className="alert alert-info d-flex mx-2 my-1 text-hyphenate"
            onClick={e => {
                e.preventDefault();
                window.location.href = removeSwishNumberUrl;
            }}>
            <SwishIcon className="icon mt-1 mr-2 flex-shrink-0" />
            <span>
                <strong>{t('siteheader_removeSwishNumberTitle')}</strong>
                <br />
                {t('siteheader_removeSwishNumberDescription')}
            </span>
        </div>
    );
};

export {
    InvoiceUnpaidAlert,
    InvoiceOverdueAlert,
    BankAccountMissingAlert,
    PayoutFailedAlert,
    PayoutReadyBankAccountMissingAlert,
    SwishMissingAlert,
    HyperwalletPayeeVerificationAlert,
    HyperwalletPayeeVerificationReviewAlert,
    FeeNettingInfoAlert,
    RemoveSwishNumberAlert
};
